import type {ComponentProps, FC} from 'react';
import NextLink from 'next/link';

import {cn} from '@/modules/foundation/shared/tailwind';

type AnchorPropsWithoutTarget = Omit<ComponentProps<'a'>, 'target'>;

export type LinkProps = AnchorPropsWithoutTarget & {
	targetBlank?: boolean;
	pathname?: string;
};

export const Link: FC<LinkProps> = ({
	href,
	targetBlank,
	className,
	children,
	pathname,
	...rest
}) => {
	const linkShouldUseAnchorTag = checkIfLinkShouldUseAnchorTag(href);

	return (
		<>
			{linkShouldUseAnchorTag ? (
				<a
					href={href ?? '#'}
					{...(targetBlank ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
					className={cn([
						'underline',
						'hover:no-underline',
						'focus-within:focus-link',
						className,
					])}
					{...rest}
				>
					{children}
				</a>
			) : (
				<NextLink
					href={href ?? '#'}
					{...(targetBlank ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
					className={cn([
						'underline',
						'hover:no-underline',
						'focus-within:focus-link',
						'transition-colors duration-200',
						'hover:text-red-600',
						pathname === href && 'border-b-[3px] border-b-red-600 text-red-600',
						className,
					])}
					{...rest}
				>
					{children}
				</NextLink>
			)}
		</>
	);
};

const checkIfLinkShouldUseAnchorTag = (href?: string) => {
	if (!href) return false;

	return (
		href?.startsWith('https://') ||
		href?.startsWith('http://') ||
		href?.startsWith('www.') ||
		href?.startsWith('mailto:') ||
		href?.startsWith('tel:')
	);
};
